import { useState, useMemo, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIntegrationStatus } from "@/hooks/use-integration-status";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DataTable, ColumnDefinition } from "@/components/ui/data-table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Search, 
  Building2,
  TrendingUp,
  TrendingDown,
  Package,
  Clock,
  CheckCircle,
  AlertTriangle,
  Download,
  Filter,
  RefreshCw,
  ExternalLink,
  Mail,
  Phone,
  MapPin,
  User,
  FileText,
  DollarSign,
  Calendar,
  Star,
  BarChart3,
  Truck,
  ShoppingCart,
  Globe,
  Plus,
  Zap,
  Lock,
  Eye,
  EyeOff,
  Copy,
  Save,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigation } from "@/components/layout/app-layout";
import { HelpButton } from "@/components/help-button";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";

// Types
interface SupplierCredential {
  id: string;
  supplierId: string;
  portalName: string;
  portalUrl?: string;
  username: string;
  password: string;
  notes?: string;
  lastUpdatedBy?: string;
  lastUpdatedAt?: Date;
}

interface SupplierContact {
  name: string;
  role: string;
  email: string;
  phone: string;
  isPrimary: boolean;
}

interface SupplierAddress {
  type: "billing" | "shipping";
  line1: string;
  line2?: string;
  city: string;
  county?: string;
  postcode: string;
  country: string;
}

interface SupplierPerformance {
  onTimeDeliveryRate: number; // Percentage
  qualityRating: number; // 1-5 stars
  averageLeadTimeDays: number;
  totalOrdersPlaced: number;
  totalOrderValue: number;
  lastOrderDate: Date;
}

interface PurchaseOrderSummary {
  id: string;
  poNumber: string;
  orderDate: Date;
  expectedDeliveryDate: Date;
  status: string;
  totalValue: number;
  currency: string;
}

interface Supplier {
  id: string;
  code: string;
  name: string;
  tradingName?: string;
  status: "active" | "inactive" | "on-hold";
  category: string;
  taxNumber?: string;
  companyRegistration?: string;
  website?: string;
  
  // Contact information
  contacts: SupplierContact[];
  addresses: SupplierAddress[];
  
  // Financial terms
  paymentTerms: string;
  creditLimit: number;
  currency: string;
  accountBalance: number; // Amount owed to supplier
  
  // Performance metrics
  performance: SupplierPerformance;
  
  // Recent purchase orders
  recentPurchaseOrders: PurchaseOrderSummary[];
  
  // Metadata
  syncedFrom: string;
  lastSyncDate: Date;
  createdDate: Date;
  notes?: string;
}

// Mock credentials data
const MOCK_CREDENTIALS: SupplierCredential[] = [
  {
    id: "cred-1",
    supplierId: "1",
    portalName: "Supplier A Order Portal",
    portalUrl: "https://portal.suppliera.com",
    username: "john.smith@company.com",
    password: "SecurePass123!",
    notes: "Use this for placing bulk orders. Contact John if issues arise.",
    lastUpdatedBy: "Sarah Johnson",
    lastUpdatedAt: new Date("2024-01-15T10:30:00")
  },
  {
    id: "cred-2",
    supplierId: "2",
    portalName: "Supplier B Inventory System",
    portalUrl: "https://inventory.supplierb.com",
    username: "purchasing@company.com",
    password: "B2BAccess2024",
    notes: "Check stock availability before ordering.",
    lastUpdatedBy: "Mike Chen",
    lastUpdatedAt: new Date("2024-01-20T14:15:00")
  }
];

// Mock data
const MOCK_SUPPLIERS: Supplier[] = [
  {
    id: "1",
    code: "SUP-001",
    name: "Supplier A Ltd",
    tradingName: "Supplier A",
    status: "active",
    category: "Raw Materials",
    taxNumber: "GB123456789",
    companyRegistration: "12345678",
    website: "https://www.suppliera.com",
    contacts: [
      {
        name: "John Smith",
        role: "Account Manager",
        email: "john@suppliera.com",
        phone: "+44 20 1234 5678",
        isPrimary: true
      },
      {
        name: "Sarah Johnson",
        role: "Sales Representative",
        email: "sarah@suppliera.com",
        phone: "+44 20 1234 5679",
        isPrimary: false
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "100 Business Park",
        line2: "Unit 5",
        city: "London",
        county: "Greater London",
        postcode: "SW1A 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "100 Business Park",
        line2: "Unit 5",
        city: "London",
        county: "Greater London",
        postcode: "SW1A 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 30 days",
    creditLimit: 50000.00,
    currency: "GBP",
    accountBalance: 15750.00,
    performance: {
      onTimeDeliveryRate: 95.5,
      qualityRating: 4.5,
      averageLeadTimeDays: 7,
      totalOrdersPlaced: 48,
      totalOrderValue: 245600.00,
      lastOrderDate: new Date("2024-01-22")
    },
    recentPurchaseOrders: [
      {
        id: "1",
        poNumber: "PO-2024-001",
        orderDate: new Date("2024-01-15"),
        expectedDeliveryDate: new Date("2024-02-15"),
        status: "partially-received",
        totalValue: 15750.00,
        currency: "GBP"
      },
      {
        id: "4",
        poNumber: "PO-2024-004",
        orderDate: new Date("2024-01-10"),
        expectedDeliveryDate: new Date("2024-01-20"),
        status: "fully-received",
        totalValue: 5400.00,
        currency: "GBP"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-06-15"),
    notes: "Preferred supplier for raw materials. Excellent quality and reliability."
  },
  {
    id: "2",
    code: "SUP-002",
    name: "Supplier B Corp",
    status: "active",
    category: "Components",
    taxNumber: "GB987654321",
    companyRegistration: "87654321",
    website: "https://www.supplierb.com",
    contacts: [
      {
        name: "Emma Davis",
        role: "Sales Manager",
        email: "emma@supplierb.com",
        phone: "+44 20 9876 5432",
        isPrimary: true
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "250 Industrial Estate",
        city: "Manchester",
        postcode: "M1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "250 Industrial Estate",
        city: "Manchester",
        postcode: "M1 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 30 days",
    creditLimit: 30000.00,
    currency: "GBP",
    accountBalance: 8500.00,
    performance: {
      onTimeDeliveryRate: 78.5,
      qualityRating: 3.5,
      averageLeadTimeDays: 12,
      totalOrdersPlaced: 32,
      totalOrderValue: 156800.00,
      lastOrderDate: new Date("2024-01-20")
    },
    recentPurchaseOrders: [
      {
        id: "2",
        poNumber: "PO-2024-002",
        orderDate: new Date("2024-01-20"),
        expectedDeliveryDate: new Date("2024-01-25"),
        status: "overdue",
        totalValue: 8500.00,
        currency: "GBP"
      },
      {
        id: "6",
        poNumber: "PO-2023-999",
        orderDate: new Date("2023-12-15"),
        expectedDeliveryDate: new Date("2024-01-05"),
        status: "cancelled",
        totalValue: 3200.00,
        currency: "GBP"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-08-20"),
    notes: "Delivery reliability issues. Monitor closely."
  },
  {
    id: "3",
    code: "SUP-003",
    name: "Global Supplies Co",
    tradingName: "Global Supplies",
    status: "active",
    category: "Equipment",
    taxNumber: "GB555123456",
    companyRegistration: "55512345",
    website: "https://www.globalsupplies.com",
    contacts: [
      {
        name: "David Chen",
        role: "International Sales",
        email: "david@globalsupplies.com",
        phone: "+44 20 5555 1234",
        isPrimary: true
      },
      {
        name: "Lisa Wong",
        role: "Customer Service",
        email: "lisa@globalsupplies.com",
        phone: "+44 20 5555 1235",
        isPrimary: false
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "Global House",
        line2: "Technology Park",
        city: "Birmingham",
        county: "West Midlands",
        postcode: "B1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "Warehouse 7",
        line2: "Distribution Centre",
        city: "Birmingham",
        county: "West Midlands",
        postcode: "B2 2BB",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 45 days",
    creditLimit: 100000.00,
    currency: "GBP",
    accountBalance: 25600.00,
    performance: {
      onTimeDeliveryRate: 92.0,
      qualityRating: 4.0,
      averageLeadTimeDays: 14,
      totalOrdersPlaced: 24,
      totalOrderValue: 456700.00,
      lastOrderDate: new Date("2024-01-22")
    },
    recentPurchaseOrders: [
      {
        id: "3",
        poNumber: "PO-2024-003",
        orderDate: new Date("2024-01-22"),
        expectedDeliveryDate: new Date("2024-02-28"),
        status: "open",
        totalValue: 25600.00,
        currency: "GBP"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-04-10"),
    notes: "Large equipment orders. Good for bulk purchases."
  },
  {
    id: "4",
    code: "SUP-004",
    name: "Food Supplier Ltd",
    status: "active",
    category: "Food Products",
    taxNumber: "GB777888999",
    companyRegistration: "77788899",
    website: "https://www.foodsupplier.com",
    contacts: [
      {
        name: "Maria Garcia",
        role: "Account Manager",
        email: "maria@foodsupplier.com",
        phone: "+44 20 7777 8888",
        isPrimary: true
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "Fresh Foods House",
        city: "Manchester",
        postcode: "M1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "456 Cold Storage Facility",
        city: "Manchester",
        postcode: "M1 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 14 days",
    creditLimit: 25000.00,
    currency: "GBP",
    accountBalance: 12300.00,
    performance: {
      onTimeDeliveryRate: 98.5,
      qualityRating: 5.0,
      averageLeadTimeDays: 2,
      totalOrdersPlaced: 156,
      totalOrderValue: 678900.00,
      lastOrderDate: new Date("2024-01-25")
    },
    recentPurchaseOrders: [
      {
        id: "5",
        poNumber: "PO-2024-005",
        orderDate: new Date("2024-01-25"),
        expectedDeliveryDate: new Date("2024-02-05"),
        status: "open",
        totalValue: 12300.00,
        currency: "GBP"
      }
    ],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-01-05"),
    notes: "Excellent for fresh food products. Very reliable and fast delivery."
  },
  {
    id: "5",
    code: "SUP-005",
    name: "Packaging Solutions Inc",
    status: "active",
    category: "Packaging",
    taxNumber: "GB444555666",
    companyRegistration: "44455566",
    website: "https://www.packagingsolutions.com",
    contacts: [
      {
        name: "Robert Taylor",
        role: "Business Development",
        email: "robert@packagingsolutions.com",
        phone: "+44 20 4444 5555",
        isPrimary: true
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "Packaging House",
        line2: "Innovation Quarter",
        city: "Leeds",
        county: "West Yorkshire",
        postcode: "LS1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "Packaging House",
        line2: "Innovation Quarter",
        city: "Leeds",
        county: "West Yorkshire",
        postcode: "LS1 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 30 days",
    creditLimit: 40000.00,
    currency: "GBP",
    accountBalance: 0.00,
    performance: {
      onTimeDeliveryRate: 88.0,
      qualityRating: 4.0,
      averageLeadTimeDays: 10,
      totalOrdersPlaced: 36,
      totalOrderValue: 189400.00,
      lastOrderDate: new Date("2024-01-10")
    },
    recentPurchaseOrders: [],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-09-12"),
    notes: "Good quality packaging materials."
  },
  {
    id: "6",
    code: "SUP-006",
    name: "Tech Components Ltd",
    status: "on-hold",
    category: "Electronics",
    taxNumber: "GB222333444",
    companyRegistration: "22233344",
    contacts: [
      {
        name: "James Wilson",
        role: "Sales Director",
        email: "james@techcomponents.com",
        phone: "+44 20 2222 3333",
        isPrimary: true
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "Tech Park",
        city: "Reading",
        postcode: "RG1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "Tech Park",
        city: "Reading",
        postcode: "RG1 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 30 days",
    creditLimit: 20000.00,
    currency: "GBP",
    accountBalance: 0.00,
    performance: {
      onTimeDeliveryRate: 65.0,
      qualityRating: 2.5,
      averageLeadTimeDays: 21,
      totalOrdersPlaced: 18,
      totalOrderValue: 89500.00,
      lastOrderDate: new Date("2023-12-05")
    },
    recentPurchaseOrders: [],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-11-20"),
    notes: "On hold due to quality issues and late deliveries. Under review."
  },
  {
    id: "7",
    code: "SUP-007",
    name: "Office Supplies Direct",
    status: "inactive",
    category: "Office Supplies",
    taxNumber: "GB111222333",
    companyRegistration: "11122233",
    contacts: [
      {
        name: "Susan Brown",
        role: "Account Manager",
        email: "susan@officesupplies.com",
        phone: "+44 20 1111 2222",
        isPrimary: true
      }
    ],
    addresses: [
      {
        type: "billing",
        line1: "Office Park",
        city: "Bristol",
        postcode: "BS1 1AA",
        country: "United Kingdom"
      },
      {
        type: "shipping",
        line1: "Office Park",
        city: "Bristol",
        postcode: "BS1 1AA",
        country: "United Kingdom"
      }
    ],
    paymentTerms: "Net 30 days",
    creditLimit: 10000.00,
    currency: "GBP",
    accountBalance: 0.00,
    performance: {
      onTimeDeliveryRate: 85.0,
      qualityRating: 3.5,
      averageLeadTimeDays: 5,
      totalOrdersPlaced: 12,
      totalOrderValue: 23400.00,
      lastOrderDate: new Date("2023-10-15")
    },
    recentPurchaseOrders: [],
    syncedFrom: "Access Financials",
    lastSyncDate: new Date("2024-01-25T10:30:00"),
    createdDate: new Date("2023-07-01"),
    notes: "Inactive - switched to alternative supplier."
  }
];

export function SuppliersPage() {
  const { navigateTo } = useNavigation();
  const integrationStatus = useIntegrationStatus();
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("suppliers");
  
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [showQuickAddDrawer, setShowQuickAddDrawer] = useState(false);
  
  // Credentials state
  const [credentials, setCredentials] = useState<SupplierCredential[]>(MOCK_CREDENTIALS);
  const [editingCredentials, setEditingCredentials] = useState(false);
  const [showPasswords, setShowPasswords] = useState<{ [key: string]: boolean }>({});
  const [credentialToDelete, setCredentialToDelete] = useState<string | null>(null);
  const [credentialForm, setCredentialForm] = useState<Partial<SupplierCredential>>({
    portalName: "",
    portalUrl: "",
    username: "",
    password: "",
    notes: ""
  });
  
  // Mock permissions - in real app, these would come from auth context
  const hasViewCredentials = true; // purchasing.suppliers.view-credentials
  const hasEditCredentials = true; // purchasing.suppliers.edit-credentials
  
  // Pagination and sorting state
  const [sortColumn, setSortColumn] = useState<keyof Supplier | string>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Calculate summary statistics
  const stats = useMemo(() => {
    const activeSuppliers = MOCK_SUPPLIERS.filter(s => s.status === "active").length;
    const totalSuppliers = MOCK_SUPPLIERS.length;
    const onHoldSuppliers = MOCK_SUPPLIERS.filter(s => s.status === "on-hold").length;
    const totalOutstanding = MOCK_SUPPLIERS.reduce((sum, s) => sum + s.accountBalance, 0);

    return { activeSuppliers, totalSuppliers, onHoldSuppliers, totalOutstanding };
  }, []);

  // Get unique categories for filter
  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(MOCK_SUPPLIERS.map(s => s.category)));
    return uniqueCategories.sort();
  }, []);

  // Filter data
  const filteredData = useMemo(() => {
    let filtered = MOCK_SUPPLIERS;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(supplier =>
        supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(supplier => supplier.status === statusFilter);
    }

    // Filter by category
    if (categoryFilter !== "all") {
      filtered = filtered.filter(supplier => supplier.category === categoryFilter);
    }

    return filtered;
  }, [searchTerm, statusFilter, categoryFilter]);

  // Sort filtered data
  const sortedData = useMemo(() => {
    const data = [...filteredData];
    
    data.sort((a, b) => {
      let aValue: any = a[sortColumn as keyof Supplier];
      let bValue: any = b[sortColumn as keyof Supplier];
      
      // Handle nested properties
      if (sortColumn === "onTimeDeliveryRate") {
        aValue = a.performance.onTimeDeliveryRate;
        bValue = b.performance.onTimeDeliveryRate;
      }
      
      // Handle date sorting
      if (aValue instanceof Date && bValue instanceof Date) {
        aValue = aValue.getTime();
        bValue = bValue.getTime();
      }
      
      // Handle string sorting (case insensitive)
      if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    
    return data;
  }, [filteredData, sortColumn, sortDirection]);

  // Paginate sorted data
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, categoryFilter]);

  // Format date
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  // Format date and time
  const formatDateTime = (date: Date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = "GBP") => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge variant="success" accent="success">Active</Badge>;
      case "inactive":
        return <Badge variant="secondary">Inactive</Badge>;
      case "on-hold":
        return <Badge variant="destructive">On Hold</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Get performance badge
  const getPerformanceBadge = (rate: number) => {
    if (rate >= 95) {
      return <Badge variant="success" accent="success">Excellent</Badge>;
    } else if (rate >= 85) {
      return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Good</Badge>;
    } else if (rate >= 75) {
      return <Badge variant="secondary" className="bg-yellow-50 text-yellow-700 border-yellow-200">Fair</Badge>;
    } else {
      return <Badge variant="destructive">Poor</Badge>;
    }
  };

  // Render star rating
  const renderStarRating = (rating: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "fill-gray-200 text-gray-200"
            }`}
          />
        ))}
        <span className="text-sm text-muted-foreground ml-1">({rating.toFixed(1)})</span>
      </div>
    );
  };

  // Handle row click
  // Handle sorting
  const handleSort = (column: keyof Supplier | string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleRowClick = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setShowDetailDrawer(true);
  };

  // Handle sync
  const handleSync = () => {
    toast.promise(
      new Promise((resolve) => setTimeout(resolve, 1500)),
      {
        loading: "Syncing with Access Financials...",
        success: "Suppliers synced successfully",
        error: "Failed to sync suppliers",
      }
    );
  };

  // Get primary contact
  const getPrimaryContact = (supplier: Supplier) => {
    return supplier.contacts.find(c => c.isPrimary) || supplier.contacts[0];
  };

  // Get billing address
  const getBillingAddress = (supplier: Supplier) => {
    return supplier.addresses.find(a => a.type === "billing") || supplier.addresses[0];
  };

  // Credentials helper functions
  const getSupplierCredentials = (supplierId: string) => {
    return credentials.filter(c => c.supplierId === supplierId);
  };

  const togglePasswordVisibility = (credentialId: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [credentialId]: !prev[credentialId]
    }));
  };

  const handleCopyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleSaveCredentials = () => {
    if (!selectedSupplier || !credentialForm.portalName || !credentialForm.username || !credentialForm.password) {
      toast.error("Please fill in all required fields");
      return;
    }

    const newCredential: SupplierCredential = {
      id: `cred-${Date.now()}`,
      supplierId: selectedSupplier.id,
      portalName: credentialForm.portalName,
      portalUrl: credentialForm.portalUrl,
      username: credentialForm.username,
      password: credentialForm.password,
      notes: credentialForm.notes,
      lastUpdatedBy: "Current User", // In real app, get from auth context
      lastUpdatedAt: new Date()
    };

    setCredentials([...credentials, newCredential]);
    setCredentialForm({
      portalName: "",
      portalUrl: "",
      username: "",
      password: "",
      notes: ""
    });
    setEditingCredentials(false);
    toast.success("Credentials saved successfully");
  };

  const handleDeleteCredential = (credentialId: string) => {
    setCredentials(credentials.filter(c => c.id !== credentialId));
    setCredentialToDelete(null);
    toast.success("Credentials deleted successfully");
  };

  const confirmDeleteCredential = () => {
    if (credentialToDelete) {
      handleDeleteCredential(credentialToDelete);
    }
  };

  // Define table columns
  const columns: ColumnDefinition<Supplier>[] = [
    {
      id: "name",
      header: "Supplier",
      sortable: true,
      cell: (supplier) => (
        <div>
          <div className="font-medium">{supplier.name}</div>
          <div className="text-sm text-muted-foreground font-mono">{supplier.code}</div>
        </div>
      ),
    },
    {
      id: "category",
      header: "Category",
      sortable: true,
      cell: (supplier) => <Badge variant="outline">{supplier.category}</Badge>,
    },
    {
      id: "contact",
      header: "Contact",
      cell: (supplier) => {
        const primaryContact = getPrimaryContact(supplier);
        return (
          <div className="text-sm">
            <div className="font-medium">{primaryContact.name}</div>
            <div className="text-muted-foreground">{primaryContact.email}</div>
          </div>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      cell: (supplier) => getStatusBadge(supplier.status),
    },
    {
      id: "onTimeDeliveryRate",
      header: "Performance",
      sortable: true,
      headerClassName: "text-center",
      cellClassName: "text-center",
      cell: (supplier) => (
        <div className="flex flex-col items-center gap-1">
          <div className="text-sm font-medium">
            {supplier.performance.onTimeDeliveryRate.toFixed(1)}%
          </div>
          {getPerformanceBadge(supplier.performance.onTimeDeliveryRate)}
        </div>
      ),
    },
    {
      id: "accountBalance",
      header: "Outstanding",
      sortable: true,
      headerClassName: "text-right",
      cellClassName: "text-right font-mono font-medium",
      cell: (supplier) =>
        supplier.accountBalance > 0 ? (
          <span className="text-blue-600">
            {formatCurrency(supplier.accountBalance, supplier.currency)}
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      id: "orders",
      header: "Orders",
      headerClassName: "text-center",
      cellClassName: "text-center",
      cell: (supplier) => (
        <Badge variant="outline">{supplier.performance.totalOrdersPlaced}</Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      headerClassName: "text-right",
      cellClassName: "text-right",
      cell: (supplier) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigateTo("purchase-orders");
            }}
          >
            <ShoppingCart className="h-4 w-4 mr-1" />
            Orders
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">Suppliers</h1>
              {integrationStatus.isFinancialsIntegrated && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  Synced from Access Financials
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-2">
              View and manage supplier information{integrationStatus.isFinancialsIntegrated ? " synced from your ERP system" : ""}
            </p>
          </div>
          <div className="flex gap-2">
            <HelpButton onClick={toggleHelp} />
            {/* <Button onClick={() => setShowQuickAddDrawer(true)} variant="default">
              <Plus className="h-4 w-4 mr-2" />
              Quick Add Supplier
            </Button> */}
            <Button onClick={handleSync} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync Now
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StaticMetricCardAlt
          title="Active Suppliers"
          value={stats.activeSuppliers.toString()}
          icon={Building2}
          variant="success"
          changeLabel={`of ${stats.totalSuppliers} total`}
        />
        <StaticMetricCardAlt
          title="On Hold"
          value={stats.onHoldSuppliers.toString()}
          icon={AlertTriangle}
          variant="error"
          changeLabel="Requires attention"
        />
        <StaticMetricCardAlt
          title="Total Outstanding"
          value={formatCurrency(stats.totalOutstanding)}
          icon={DollarSign}
          variant="info"
          changeLabel="Amount owed to suppliers"
        />
        <StaticMetricCardAlt
          title="Categories"
          value={categories.length.toString()}
          icon={Package}
          variant="info"
          changeLabel="Supplier categories"
        />
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Supplier Directory</CardTitle>
              <CardDescription>
                View supplier information synced from Access Financials
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, code, or category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="on-hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Data Table */}
          <DataTable
            data={paginatedData}
            columns={columns}
            sortConfig={{ column: sortColumn, direction: sortDirection }}
            onSort={handleSort}
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={sortedData.length}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            onRowClick={handleRowClick}
            emptyState={
              <div className="text-center py-12">
                <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No suppliers found</h3>
                <p className="text-sm text-muted-foreground">
                  Try adjusting your search or filters
                </p>
              </div>
            }
            striped
          />
        </CardContent>
      </Card>

      {/* Detail Drawer */}
      <Sheet open={showDetailDrawer} onOpenChange={setShowDetailDrawer}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto p-3">
          {selectedSupplier && (
            <>
              <SheetHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <SheetTitle>{selectedSupplier.name}</SheetTitle>
                    {getStatusBadge(selectedSupplier.status)}
                  </div>
                </div>
                <SheetDescription>
                  {selectedSupplier.code} • {selectedSupplier.category}
                </SheetDescription>
              </SheetHeader>

              <Tabs defaultValue="overview" className="mt-6">
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="contacts">Contacts</TabsTrigger>
                  <TabsTrigger value="performance">Performance</TabsTrigger>
                  <TabsTrigger value="orders">Orders</TabsTrigger>
                  {hasViewCredentials && (
                    <TabsTrigger value="credentials">
                      <Lock className="h-4 w-4 mr-2" />
                      Credentials
                    </TabsTrigger>
                  )}
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-6">
                  {/* Sync Information */}
                  <div className="bg-muted/50 p-3 rounded-lg text-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Last synced:</span>
                        <span className="font-medium">{formatDateTime(selectedSupplier.lastSyncDate)}</span>
                      </div>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {selectedSupplier.syncedFrom}
                      </Badge>
                    </div>
                  </div>

                  {/* Basic Information */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Company Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Supplier Code</Label>
                        <p className="font-medium font-mono">{selectedSupplier.code}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Legal Name</Label>
                        <p className="font-medium">{selectedSupplier.name}</p>
                      </div>
                      {selectedSupplier.tradingName && (
                        <div>
                          <Label className="text-muted-foreground">Trading Name</Label>
                          <p className="font-medium">{selectedSupplier.tradingName}</p>
                        </div>
                      )}
                      <div>
                        <Label className="text-muted-foreground">Category</Label>
                        <p className="font-medium">{selectedSupplier.category}</p>
                      </div>
                      {selectedSupplier.taxNumber && (
                        <div>
                          <Label className="text-muted-foreground">Tax Number</Label>
                          <p className="font-medium font-mono">{selectedSupplier.taxNumber}</p>
                        </div>
                      )}
                      {selectedSupplier.companyRegistration && (
                        <div>
                          <Label className="text-muted-foreground">Company Registration</Label>
                          <p className="font-medium font-mono">{selectedSupplier.companyRegistration}</p>
                        </div>
                      )}
                      {selectedSupplier.website && (
                        <div className="col-span-2">
                          <Label className="text-muted-foreground">Website</Label>
                          <a 
                            href={selectedSupplier.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="font-medium text-primary hover:underline flex items-center gap-1"
                          >
                            <Globe className="h-3 w-3" />
                            {selectedSupplier.website}
                          </a>
                        </div>
                      )}
                      <div>
                        <Label className="text-muted-foreground">Member Since</Label>
                        <p className="font-medium">{formatDate(selectedSupplier.createdDate)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Financial Information */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Financial Terms
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Payment Terms</Label>
                        <p className="font-medium">{selectedSupplier.paymentTerms}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Currency</Label>
                        <p className="font-medium font-mono">{selectedSupplier.currency}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Credit Limit</Label>
                        <p className="font-medium text-lg">
                          {formatCurrency(selectedSupplier.creditLimit, selectedSupplier.currency)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Current Outstanding</Label>
                        <p className="font-medium text-lg text-blue-600">
                          {formatCurrency(selectedSupplier.accountBalance, selectedSupplier.currency)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Addresses */}
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Addresses
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedSupplier.addresses.map((address, idx) => (
                        <div key={idx} className="bg-muted/50 p-3 rounded-lg">
                          <div className="font-medium mb-2 capitalize">{address.type} Address</div>
                          <div className="text-sm space-y-0.5">
                            <p>{address.line1}</p>
                            {address.line2 && <p>{address.line2}</p>}
                            <p>{address.city}</p>
                            {address.county && <p>{address.county}</p>}
                            <p>{address.postcode}</p>
                            <p>{address.country}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Notes */}
                  {selectedSupplier.notes && (
                    <div>
                      <h3 className="font-semibold mb-3">Notes</h3>
                      <p className="text-sm bg-muted p-3 rounded-md">{selectedSupplier.notes}</p>
                    </div>
                  )}
                </TabsContent>

                {/* Contacts Tab */}
                <TabsContent value="contacts" className="space-y-4">
                  {selectedSupplier.contacts.map((contact, idx) => (
                    <Card key={idx}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-lg">{contact.name}</CardTitle>
                            <CardDescription>{contact.role}</CardDescription>
                          </div>
                          {contact.isPrimary && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              Primary Contact
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                            {contact.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <a href={`tel:${contact.phone}`} className="text-primary hover:underline">
                            {contact.phone}
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>

                {/* Performance Tab */}
                <TabsContent value="performance" className="space-y-6">
                  {/* Performance Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">On-Time Delivery</CardTitle>
                        <Clock className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {selectedSupplier.performance.onTimeDeliveryRate.toFixed(1)}%
                        </div>
                        <div className="mt-2">
                          {getPerformanceBadge(selectedSupplier.performance.onTimeDeliveryRate)}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Quality Rating</CardTitle>
                        <Star className="h-4 w-4 text-yellow-400" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {selectedSupplier.performance.qualityRating.toFixed(1)}/5.0
                        </div>
                        <div className="mt-2">
                          {renderStarRating(selectedSupplier.performance.qualityRating)}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Avg Lead Time</CardTitle>
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {selectedSupplier.performance.averageLeadTimeDays}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          days
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
                        <Package className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {selectedSupplier.performance.totalOrdersPlaced}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          purchase orders
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Order Value */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium">Total Order Value</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-blue-600">
                        {formatCurrency(selectedSupplier.performance.totalOrderValue, selectedSupplier.currency)}
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        Last order: {formatDate(selectedSupplier.performance.lastOrderDate)}
                      </p>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Orders Tab */}
                <TabsContent value="orders" className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">Recent Purchase Orders</h3>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigateTo("purchase-orders")}
                    >
                      View All Orders
                    </Button>
                  </div>

                  {selectedSupplier.recentPurchaseOrders.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No recent purchase orders</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedSupplier.recentPurchaseOrders.map((po) => (
                        <Card key={po.id}>
                          <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-mono font-bold text-lg">{po.poNumber}</div>
                                <div className="text-sm text-muted-foreground mt-1">
                                  Ordered: {formatDate(po.orderDate)}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  Expected: {formatDate(po.expectedDeliveryDate)}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-bold text-xl mb-2">
                                  {formatCurrency(po.totalValue, po.currency)}
                                </div>
                                {getStatusBadge(po.status)}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Credentials Tab */}
                {hasViewCredentials && (
                  <TabsContent value="credentials" className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold flex items-center gap-2">
                          <Lock className="h-4 w-4" />
                          Portal Access Credentials
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Securely stored login credentials for supplier portals
                        </p>
                      </div>
                      {hasEditCredentials && !editingCredentials && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setEditingCredentials(true)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Credentials
                        </Button>
                      )}
                    </div>

                    {/* Add/Edit Form */}
                    {editingCredentials && hasEditCredentials && (
                      <Card className="border-blue-200 bg-blue-50/50">
                        <CardHeader>
                          <CardTitle className="text-base">Add New Credentials</CardTitle>
                          <CardDescription>
                            All fields marked with * are required
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="portalName">Portal Name *</Label>
                              <Input
                                id="portalName"
                                placeholder="e.g., Order Management Portal"
                                value={credentialForm.portalName}
                                onChange={(e) => setCredentialForm({ ...credentialForm, portalName: e.target.value })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="portalUrl">Portal URL</Label>
                              <Input
                                id="portalUrl"
                                placeholder="https://portal.supplier.com"
                                value={credentialForm.portalUrl}
                                onChange={(e) => setCredentialForm({ ...credentialForm, portalUrl: e.target.value })}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="username">Username *</Label>
                              <Input
                                id="username"
                                placeholder="username@company.com"
                                value={credentialForm.username}
                                onChange={(e) => setCredentialForm({ ...credentialForm, username: e.target.value })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="password">Password *</Label>
                              <Input
                                id="password"
                                type="password"
                                placeholder="••••••••"
                                value={credentialForm.password}
                                onChange={(e) => setCredentialForm({ ...credentialForm, password: e.target.value })}
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="notes">Notes</Label>
                            <Input
                              id="notes"
                              placeholder="Additional information or instructions"
                              value={credentialForm.notes}
                              onChange={(e) => setCredentialForm({ ...credentialForm, notes: e.target.value })}
                            />
                          </div>

                          <div className="flex gap-2 justify-end">
                            <Button
                              variant="outline"
                              onClick={() => {
                                setEditingCredentials(false);
                                setCredentialForm({
                                  portalName: "",
                                  portalUrl: "",
                                  username: "",
                                  password: "",
                                  notes: ""
                                });
                              }}
                            >
                              Cancel
                            </Button>
                            <Button onClick={handleSaveCredentials}>
                              <Save className="h-4 w-4 mr-2" />
                              Save Credentials
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Credentials List */}
                    {selectedSupplier && getSupplierCredentials(selectedSupplier.id).length === 0 && !editingCredentials ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Lock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <h3 className="text-lg font-semibold mb-2">No credentials stored</h3>
                        <p className="text-sm mb-4">
                          Add portal login credentials to securely store supplier access information
                        </p>
                        {hasEditCredentials && (
                          <Button 
                            variant="outline"
                            onClick={() => setEditingCredentials(true)}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add First Credential
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {selectedSupplier && getSupplierCredentials(selectedSupplier.id).map((credential) => (
                          <Card key={credential.id}>
                            <CardHeader>
                              <div className="flex items-center justify-between">
                                <div>
                                  <CardTitle className="text-base flex items-center gap-2">
                                    <Lock className="h-4 w-4 text-muted-foreground" />
                                    {credential.portalName}
                                  </CardTitle>
                                  {credential.portalUrl && (
                                    <CardDescription className="mt-1">
                                      <a 
                                        href={credential.portalUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline flex items-center gap-1"
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                        {credential.portalUrl}
                                      </a>
                                    </CardDescription>
                                  )}
                                </div>
                                {hasEditCredentials && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setCredentialToDelete(credential.id)}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                )}
                              </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                              {/* Username */}
                              <div className="space-y-2">
                                <Label className="text-muted-foreground">Username</Label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={credential.username}
                                    readOnly
                                    className="font-mono bg-muted"
                                  />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleCopyToClipboard(credential.username, "Username")}
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>

                              {/* Password */}
                              <div className="space-y-2">
                                <Label className="text-muted-foreground">Password</Label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type={showPasswords[credential.id] ? "text" : "password"}
                                    value={credential.password}
                                    readOnly
                                    className="font-mono bg-muted"
                                  />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => togglePasswordVisibility(credential.id)}
                                  >
                                    {showPasswords[credential.id] ? (
                                      <EyeOff className="h-4 w-4" />
                                    ) : (
                                      <Eye className="h-4 w-4" />
                                    )}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleCopyToClipboard(credential.password, "Password")}
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>

                              {/* Notes */}
                              {credential.notes && (
                                <div className="space-y-2">
                                  <Label className="text-muted-foreground">Notes</Label>
                                  <p className="text-sm bg-muted p-3 rounded-md">
                                    {credential.notes}
                                  </p>
                                </div>
                              )}

                              {/* Last Updated */}
                              {credential.lastUpdatedBy && credential.lastUpdatedAt && (
                                <div className="text-xs text-muted-foreground pt-2 border-t">
                                  Last updated by {credential.lastUpdatedBy} on {formatDateTime(credential.lastUpdatedAt)}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}

                    {/* Security Notice */}
                    <Card className="border-yellow-200 bg-yellow-50/50">
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                          <div className="text-sm">
                            <p className="font-medium text-yellow-900 mb-1">Security Notice</p>
                            <p className="text-yellow-800">
                              Credentials are encrypted and stored securely. Only users with appropriate permissions can view or edit this information.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                )}
              </Tabs>

              {/* Actions */}
              <div className="flex gap-2 pt-6 border-t mt-6">
                <Button 
                  className="flex-1"
                  onClick={() => {
                    navigateTo("purchase-orders");
                    setShowDetailDrawer(false);
                  }}
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  View Purchase Orders
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Quick Add Supplier Drawer */}
      <Sheet open={showQuickAddDrawer} onOpenChange={setShowQuickAddDrawer}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center gap-2">
              <SheetTitle>Quick Add Supplier</SheetTitle>
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                <Zap className="h-3 w-3 mr-1" />
                Future Implementation
              </Badge>
            </div>
            <SheetDescription>
              This will be an integrated micro frontend for quickly adding suppliers
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Vision Card */}
            <Card className="border-purple-200 bg-purple-50/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="h-5 w-5 text-purple-600" />
                  Micro Frontend Integration
                </CardTitle>
                <CardDescription>
                  This feature will be powered by a dedicated micro frontend application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <Building2 className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Standalone Application</div>
                      <div className="text-sm text-muted-foreground">
                        Independent micro frontend that can be developed and deployed separately
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <Zap className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Quick Data Entry</div>
                      <div className="text-sm text-muted-foreground">
                        Streamlined form for rapid supplier onboarding with smart defaults
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <RefreshCw className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Real-time Sync</div>
                      <div className="text-sm text-muted-foreground">
                        Automatically syncs with Access Financials and updates the main supplier list
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-purple-100 p-2 mt-0.5">
                      <CheckCircle className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium">Validation & Verification</div>
                      <div className="text-sm text-muted-foreground">
                        Built-in validation for tax numbers, company registration, and duplicate detection
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview of Future Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Form Preview</CardTitle>
                <CardDescription>
                  A glimpse of the quick add interface (non-functional)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 opacity-60 pointer-events-none">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Supplier Name *</Label>
                    <Input placeholder="Enter supplier name" disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Supplier Code</Label>
                    <Input placeholder="Auto-generated" disabled />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select disabled>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tax Number</Label>
                    <Input placeholder="e.g., GB123456789" disabled />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Primary Contact Email *</Label>
                  <Input type="email" placeholder="contact@supplier.com" disabled />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <Input placeholder="+44 20 1234 5678" disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Terms</Label>
                    <Select disabled>
                      <SelectTrigger>
                        <SelectValue placeholder="Select terms" />
                      </SelectTrigger>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input placeholder="Street address" disabled className="mb-2" />
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="City" disabled />
                    <Input placeholder="Postcode" disabled />
                    <Input placeholder="Country" disabled />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Technical Details */}
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  Technical Architecture
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Module Federation:</span>
                    <span className="text-muted-foreground">Webpack 5 Module Federation for runtime integration</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Independent Deployment:</span>
                    <span className="text-muted-foreground">Can be updated without redeploying main app</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Shared Context:</span>
                    <span className="text-muted-foreground">Access to authentication and integration status</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                    <span className="font-medium">Event Communication:</span>
                    <span className="text-muted-foreground">Publishes events when suppliers are added</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Call to Action */}
            <div className="bg-muted p-4 rounded-lg text-center">
              <p className="text-sm text-muted-foreground mb-3">
                This feature is part of the roadmap for enhanced supplier management
              </p>
              <Button variant="outline" disabled>
                <Building2 className="h-4 w-4 mr-2" />
                Coming Soon
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Help Drawer */}
      <ContextualHelpDrawer
        open={isHelpOpen}
        onOpenChange={setIsHelpOpen}
        helpContent={helpContent}
      />

      {/* Delete Credential Confirmation Dialog */}
      <AlertDialog open={!!credentialToDelete} onOpenChange={(open) => !open && setCredentialToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Credentials?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete these credentials? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteCredential} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
